/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


/*
 * Step 2: Personal Information — name, email, phone, DOB, license info, address
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { User, CreditCard, MapPin, ChevronDown, ChevronUp, ArrowLeft, ArrowRight, Sparkles, Building2 } from 'lucide-react';
import type { DriverFormData } from './types';

interface PersonalInfoStepProps {
  formData: DriverFormData;
  setFormData: React.Dispatch<React.SetStateAction<DriverFormData>>;
  smsConsent: boolean;
  setSmsConsent: (v: boolean) => void;
  licenseAnalysisSuccess: boolean;
  isLicenseInfoExpanded: boolean;
  setIsLicenseInfoExpanded: (v: boolean) => void;
  isAddressInfoExpanded: boolean;
  setIsAddressInfoExpanded: (v: boolean) => void;
  onNext: () => void;
  onPrevious: () => void;
  // When true, the built-in Next button is hidden so the caller can render its own
  // (e.g. CreateOwnerPage adds a password section and a single Next below it).
  hideNextButton?: boolean;
  // When true, also hide the built-in Previous button so the caller can place
  // both nav buttons together below additional content.
  hideNavButtons?: boolean;
  // When true, show an optional Company Name field at the top (owner / partner registration).
  showCompanyName?: boolean;
  // When true, hide the collapsible Driver License Information section
  // (e.g. owner / partner registration when the license scan step is hidden).
  hideLicenseInfo?: boolean;
  // When true, hide the collapsible Address Information section
  // (e.g. owner / partner registration where the address is collected on a dedicated step).
  hideAddressInfo?: boolean;
  // Overrides the heading for the personal-info section/step
  // (e.g. "Owner Information" for owner / partner registration).
  infoTitle?: string;
  // Called when the email field loses focus. Owner registration uses it to find out whether the
  // address is already registered while it is still on screen; driver registration leaves it unset,
  // because there an existing address is a normal merge rather than a mistake.
  onEmailBlur?: () => void;
}

export const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  formData, setFormData,
  smsConsent, setSmsConsent,
  licenseAnalysisSuccess,
  isLicenseInfoExpanded, setIsLicenseInfoExpanded,
  isAddressInfoExpanded, setIsAddressInfoExpanded,
  onNext, onPrevious, hideNextButton = false, hideNavButtons = false,
  showCompanyName = false, hideLicenseInfo = false, hideAddressInfo = false,
  infoTitle, onEmailBlur,
}) => {
  const { t } = useTranslation();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Keep the three DOB parts in local state so partial selections persist.
  // dateOfBirth is only a full ISO date, so deriving the parts from it directly
  // would discard any single pick until all three are chosen.
  const [birthParts, setBirthParts] = React.useState(() => {
    if (formData.dateOfBirth) {
      const d = new Date(formData.dateOfBirth + 'T00:00:00');
      if (!Number.isNaN(d.getTime())) {
        return { month: String(d.getMonth() + 1), day: String(d.getDate()), year: String(d.getFullYear()) };
      }
    }
    return { month: '', day: '', year: '' };
  });

  // Sync local parts when dateOfBirth is set externally (e.g. OCR auto-fill).
  React.useEffect(() => {
    if (!formData.dateOfBirth) return;
    const d = new Date(formData.dateOfBirth + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return;
    const month = String(d.getMonth() + 1);
    const day = String(d.getDate());
    const year = String(d.getFullYear());
    setBirthParts(prev =>
      prev.month === month && prev.day === day && prev.year === year
        ? prev
        : { month, day, year });
  }, [formData.dateOfBirth]);

  const handleDateSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const next = {
      ...birthParts,
      ...(name === 'birthMonth' ? { month: value } : {}),
      ...(name === 'birthDay' ? { day: value } : {}),
      ...(name === 'birthYear' ? { year: value } : {}),
    };
    setBirthParts(next);
    const month = parseInt(next.month) || 0;
    let day = parseInt(next.day) || 0;
    const year = parseInt(next.year) || 0;
    if (month && day && year) {
      const daysInMonth = new Date(year, month, 0).getDate();
      if (day > daysInMonth) day = daysInMonth;
      // Build the ISO date manually to avoid the UTC shift from toISOString().
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setFormData(prev => ({ ...prev, dateOfBirth: iso }));
    } else {
      setFormData(prev => ({ ...prev, dateOfBirth: '' }));
    }
  };

  const handleNext = () => {
    if (!formData.firstName.trim()) { toast.error(t('wizard.validation.firstName', 'First name is required')); return; }
    if (!formData.lastName.trim()) { toast.error(t('wizard.validation.lastName', 'Last name is required')); return; }
    if (!formData.email.trim()) { toast.error(t('wizard.validation.emailRequired', 'Email is required')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error(t('wizard.validation.emailInvalid', 'Invalid email')); return; }
    const phoneDigits = (formData.phoneNumber || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error(t('wizard.validation.phoneDigits', 'Phone must contain at least 10 digits'));
      return;
    }
    onNext();
  };

  const inputClass = (field: string) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      licenseAnalysisSuccess && (formData as any)[field] ? 'border-green-400 bg-green-50' : 'border-gray-300'
    }`;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold text-gray-900 mb-1">
          {infoTitle || t('modals.driverWizard.step2Title', 'Personal Information')}
        </h4>
        <p className="text-sm text-gray-500">
          {licenseAnalysisSuccess
            ? t('wizard.info.descriptionAutoFilled', 'We extracted your information. Please verify.')
            : t('wizard.info.description', 'Please fill in your personal information.')}
        </p>
      </div>

      {licenseAnalysisSuccess && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-500" />
          <p className="text-sm text-blue-700">{t('wizard.info.autoFilledNotice', 'Fields were auto-filled from your license.')}</p>
        </div>
      )}

      {/* Personal Info */}
      <div className="p-3 sm:p-4 bg-gray-50 rounded-lg space-y-3">
        <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-500" /> {infoTitle || t('wizard.personal.title', 'Personal Information')}
        </h5>
        {showCompanyName && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-blue-500" /> {t('wizard.personal.companyName', 'Company Name')}
            </label>
            <input type="text" name="companyName" autoComplete="organization" value={formData.companyName} onChange={handleInputChange}
              placeholder={t('wizard.personal.companyNamePlaceholder', 'Optional')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('wizard.personal.firstName', 'First Name')} <span className="text-red-500">*</span>
            </label>
            <input type="text" name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleInputChange} className={inputClass('firstName')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('wizard.personal.lastName', 'Last Name')} <span className="text-red-500">*</span>
            </label>
            <input type="text" name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleInputChange} className={inputClass('lastName')} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('wizard.personal.email', 'Email')} <span className="text-red-500">*</span>
          </label>
          <input type="email" name="email" autoComplete="email" value={formData.email} onChange={handleInputChange}
            onBlur={onEmailBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('wizard.personal.phone', 'Phone')} <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange}
              placeholder={t('wizard.personal.phonePlaceholder', '(XXX) XXX-XXXX')}
              autoComplete="tel"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            <label className="flex items-center gap-1.5 cursor-not-allowed whitespace-nowrap opacity-60" title="SMS consent is managed by Twilio (STOP/START)">
              <input type="checkbox" checked={smsConsent} disabled
                className="w-4 h-4 rounded border-gray-300 text-gray-400" />
              <span className="text-sm text-gray-500">{t('wizard.personal.smsConsent', 'SMS')}</span>
            </label>
          </div>
        </div>

        {/* DOB */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.dateOfBirth', 'Date of Birth')}</label>
          <div className="grid grid-cols-3 gap-2">
            <select name="birthMonth" value={birthParts.month} onChange={handleDateSelectChange}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="">{t('wizard.personal.month', 'Month')}</option>
              {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
            </select>
            <select name="birthDay" value={birthParts.day} onChange={handleDateSelectChange}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="">{t('wizard.personal.day', 'Day')}</option>
              {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
            </select>
            <select name="birthYear" value={birthParts.year} onChange={handleDateSelectChange}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              <option value="">{t('wizard.personal.year', 'Year')}</option>
              {Array.from({ length: 120 }, (_, i) => { const y = new Date().getFullYear() - 18 - i; return <option key={y} value={y}>{y}</option>; })}
            </select>
          </div>
        </div>
      </div>

      {/* License Info - Collapsible */}
      {!hideLicenseInfo && (
      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <button type="button" onClick={() => setIsLicenseInfoExpanded(!isLicenseInfoExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-blue-500" /> {t('wizard.license.infoTitle', 'Driver License Information')}
          </span>
          {isLicenseInfoExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </button>
        {isLicenseInfoExpanded && (
          <div className="p-3 sm:p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.licenseNumber', 'License Number')}</label>
                <input type="text" name="driverLicenseNumber" value={formData.driverLicenseNumber} onChange={handleInputChange} className={inputClass('driverLicenseNumber')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.issuingState', 'Issuing State')}</label>
                <input type="text" name="driverLicenseState" value={formData.driverLicenseState} onChange={handleInputChange} maxLength={2}
                  className={`${inputClass('driverLicenseState')} uppercase`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.expirationDate', 'Expiration Date')}</label>
                <input type="date" name="driverLicenseExpirationDate" value={formData.driverLicenseExpirationDate} onChange={handleInputChange}
                  className={inputClass('driverLicenseExpirationDate')} />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Address Info - Collapsible */}
      {!hideAddressInfo && (
      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <button type="button" onClick={() => setIsAddressInfoExpanded(!isAddressInfoExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-500" /> {t('wizard.license.addressTitle', 'Address Information')}
          </span>
          {isAddressInfoExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </button>
        {isAddressInfoExpanded && (
          <div className="p-3 sm:p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.addressLine1', 'Address Line 1')}</label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} className={inputClass('addressLine1')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.addressLine2', 'Address Line 2')}</label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.city', 'City')}</label>
                <input type="text" name="city" value={formData.city} onChange={handleInputChange} className={inputClass('city')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.state', 'State')}</label>
                <input type="text" name="state" value={formData.state} onChange={handleInputChange} maxLength={2}
                  className={`${inputClass('state')} uppercase`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wizard.license.zipCode', 'ZIP Code')}</label>
                <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} className={inputClass('zipCode')} />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {!hideNavButtons && (
        <div className="flex justify-between pt-2">
          <button onClick={onPrevious} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {t('wizard.buttons.previous', 'Previous')}
          </button>
          {!hideNextButton && (
            <button onClick={handleNext} className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1">
              {t('wizard.buttons.next', 'Next')} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
